import type { HistoryEventSlice } from './types.js';
import { DIRECTOR_OUTPUT_SCHEMA_VERSION } from './errors.js';
import type {
  AssumptionOutput,
  CharacterOptionsOutput,
  ConnectedQuestionsOutput,
  RuntimeSceneOutput,
  SummaryOutput,
  WorldDevicesOutput,
} from './schemas.js';

export function fallbackConnectedQuestions(): ConnectedQuestionsOutput {
  return { schemaVersion: DIRECTOR_OUTPUT_SCHEMA_VERSION, questions: [], fallbackQuestionIds: [] };
}

export function fallbackAssumption(fieldPath: string): AssumptionOutput {
  return {
    schemaVersion: DIRECTOR_OUTPUT_SCHEMA_VERSION,
    fieldPath,
    primary: {
      value: null,
      assumption: 'Local fallback — no provider suggestion available.',
      confidence: 'low',
      contextSourceIds: [],
    },
    alternatives: [],
  };
}

export function fallbackWorldDevices(seed: string): WorldDevicesOutput {
  const devices = Array.from({ length: 20 }, (_, i) => ({
    deviceId: `local-device-${i + 1}`,
    category: 'local',
    title: `Fallback device ${i + 1}`,
    description: 'Deterministic local plot device placeholder.',
    trigger: 'ambient',
    eligibleActorIds: [],
    consequenceRange: 'minor',
    intensity: 'mild' as const,
    contextSourceIds: [],
    safetyStatus: 'reviewed' as const,
  }));
  return { schemaVersion: DIRECTOR_OUTPUT_SCHEMA_VERSION, devices, generationSeed: seed };
}

export function fallbackCharacterOptions(): CharacterOptionsOutput {
  return {
    schemaVersion: DIRECTOR_OUTPUT_SCHEMA_VERSION,
    appearanceOptions: [
      { slot: 'hair' as const, label: 'Default', description: 'Simple, period-neutral style.' },
    ],
    preservedFields: [],
  };
}

export function fallbackRuntimeScene(): RuntimeSceneOutput {
  return {
    schemaVersion: DIRECTOR_OUTPUT_SCHEMA_VERSION,
    dialogue: [],
    eventCandidates: [],
    actorEffects: [],
    summaryText: 'Quiet moment — no new activity.',
    noPersistence: true,
  };
}

/** Deterministically summarize provided persisted events — never fabricate activity. */
export function fallbackSummary(
  eventIds: string[],
  events: HistoryEventSlice[] = [],
): SummaryOutput {
  const matched = events
    .filter((e) => eventIds.includes(e.eventId))
    .sort((a, b) => a.occurredAt.localeCompare(b.occurredAt));

  if (matched.length === 0) {
    return {
      schemaVersion: DIRECTOR_OUTPUT_SCHEMA_VERSION,
      observedFacts: 'No persisted events matched the requested ids.',
      eventIds,
    };
  }

  const observedFacts = matched
    .map((e) => `${e.occurredAt}: ${e.summary}`)
    .join(' ');

  return {
    schemaVersion: DIRECTOR_OUTPUT_SCHEMA_VERSION,
    observedFacts,
    directorInterpretation: 'Local fallback summary from persisted events.',
    eventIds: matched.map((e) => e.eventId),
  };
}

